/**
 * Score Query Functions for Dashboard
 * 
 * Reads calculated scores from user_scores table for display.
 */

import { supabase } from '@/integrations/supabase/client';
import type { PillarScore } from './types';

export async function getOverallScore(
  userId: string,
  teamId: string
): Promise<{ score: number | null; isPreliminary: boolean }> {
  const { data, error } = await supabase
    .from('user_scores')
    .select('score_value, score_breakdown')
    .eq('user_id', userId)
    .eq('team_id', teamId)
    .eq('entity_type', 'overall')
    .is('entity_id', null)
    .maybeSingle();

  if (error || !data) return { score: null, isPreliminary: true };

  const breakdown = data.score_breakdown as Record<string, any> | null;
  return {
    score: Number(data.score_value),
    isPreliminary: breakdown?.is_preliminary ?? true
  };
}

export async function getPillarScores(
  userId: string,
  teamId: string
): Promise<PillarScore[]> {
  const { data, error } = await supabase
    .from('user_scores')
    .select('score_value, score_breakdown, entity_id')
    .eq('user_id', userId)
    .eq('team_id', teamId)
    .eq('entity_type', 'pillar');

  if (error || !data || data.length === 0) return [];

  // Fetch pillar definitions
  const pillarIds = data.map(d => d.entity_id).filter(Boolean) as string[];
  const { data: pillars } = await supabase
    .from('scoring_pillars')
    .select('id, pillar_number, title')
    .in('id', pillarIds);

  const pillarLookup = new Map((pillars || []).map(p => [p.id, p]));

  return data.map(row => {
    const pillar = pillarLookup.get(row.entity_id!);
    const breakdown = row.score_breakdown as Record<string, any> | null;
    return {
      pillarId: row.entity_id!,
      pillarNumber: pillar?.pillar_number ?? 0,
      pillarName: pillar?.title || '',
      score: Number(row.score_value),
      coverage: breakdown?.coverage_percent ?? 0,
      sectionsScored: breakdown?.sections_scored ?? 0,
      sectionsTotal: breakdown?.sections_total ?? 0
    };
  }).sort((a, b) => a.pillarNumber - b.pillarNumber);
}
